import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { DashLayout } from './shell/dash-layout';
import { RetiredRoute } from './shell/retired-route';
import { HomeScreen } from './screens/home/home-screen';
import { NotFoundScreen } from './screens/not-found/not-found-screen';
import { ListSkeleton } from './shell/skeleton';

// Route-level code splitting (perf: the entry chunk carried every screen). The landing
// screen and not-found stay in the entry bundle; every other screen is its own chunk,
// fetched on first navigation.
const BookingsScreen = lazy(() => import('./screens/bookings/bookings-screen').then((m) => ({ default: m.BookingsScreen })));
const SavedScreen = lazy(() => import('./screens/saved/saved-screen').then((m) => ({ default: m.SavedScreen })));
const ReviewsScreen = lazy(() => import('./screens/reviews/reviews-screen').then((m) => ({ default: m.ReviewsScreen })));
const AccountScreen = lazy(() => import('./screens/account/account-screen').then((m) => ({ default: m.AccountScreen })));
const HelpScreen = lazy(() => import('./screens/help/help-screen').then((m) => ({ default: m.HelpScreen })));
const MessagesScreen = lazy(() => import('./screens/messages/messages-screen').then((m) => ({ default: m.MessagesScreen })));
const ClaimsScreen = lazy(() => import('./screens/claims/claims-screen').then((m) => ({ default: m.ClaimsScreen })));

// A lazy screen's boundary — shaped like the page that is arriving (a blank pane
// reads as a broken navigation). Navigations run in a transition, so the current
// screen normally stays up until the next one's chunk is in.
function lazyRoute(element: ReactElement): ReactElement {
  return <Suspense fallback={<ListSkeleton />}>{element}</Suspense>;
}

// Pure wiring (CB-D2): the customer dashboard route table of PRD v2.5 §7.3.2, inside
// the persistent shell (DashLayout: rail · top bar · tab bar, DC60).
//   /                 Home
//   /bookings         My bookings (`?tab=`); `?b=<code>` opens the booking sheet (route 3, DC62)
//   /saved /reviews   Mine
//   /account          Account (replaces /profile; DC66)
//   /help             Help — FAQ from the CMS, My problems, contact (DC66)
//   /messages /claims under More (DR-38)
// Retired paths keep resolving: /profile → /account, /notifications → / with the tray
// open (FR-C28), /bookings/:code → /bookings?b=:code. Route 4 (/bookings/new) opens the
// new-booking wizard over My bookings (DC63; §16 Q27 answered by gate 11: the dashboard
// hosts its own flow, duplicated from the website stepper per CB-D3).
export const routes: RouteObject[] = [
  {
    element: <DashLayout />,
    children: [
      { path: '/', element: <HomeScreen /> },
      { path: '/bookings', element: lazyRoute(<BookingsScreen />) },
      { path: '/bookings/new', element: lazyRoute(<BookingsScreen wizard />) },
      { path: '/bookings/:code', element: <RetiredRoute to="/bookings" carryParam="code" as="b" /> },
      { path: '/saved', element: lazyRoute(<SavedScreen />) },
      { path: '/reviews', element: lazyRoute(<ReviewsScreen />) },
      { path: '/account', element: lazyRoute(<AccountScreen />) },
      { path: '/help', element: lazyRoute(<HelpScreen />) },
      { path: '/messages', element: lazyRoute(<MessagesScreen />) },
      { path: '/claims', element: lazyRoute(<ClaimsScreen />) },
      { path: '/profile', element: <RetiredRoute to="/account" /> },
      { path: '/notifications', element: <RetiredRoute to="/" state={{ tray: true }} /> },
      { path: '*', element: <NotFoundScreen /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
